/**
 * 감사 로그 (API Audit Log) 관리 API 서비스
 * - 백엔드 명세: ADMIN_AUDIT_LOG_API.md (v1.4, 2026-05-20)
 * - 경로: /api/admin/audit/api-logs/*
 * - 관리자(ADMIN) 권한 JWT 필수
 *
 * 주요 기능
 *  - 목록 조회 (GET /search): 쿼리스트링 기반 다중 필터 + 페이지네이션
 *    ※ v1.2 부터 POST → GET 으로 환원 (POST 사용 시 감사 엔드포인트 자체가 또 적재되는 폭주 회피)
 *    ※ /api/admin/audit/** 경로는 백엔드 화이트리스트로 적재 제외
 *  - 상세 조회 (GET /{logSeq}): 요청/응답 본문, 헤더, 스택트레이스 포함
 *  - 통계 조회 (GET /stats): 액터별/성공실패 카운트, 실패 URI Top N
 *
 * 버전 노트
 *  - v1.3: 보존기간 지난 데이터의 S3 아카이브 자동 fallback (프론트 변경 없음)
 *  - v1.4: `apiSummary` 필드 추가 — `@Operation summary`+`@Tag name` 조합의 어드민 가독용 라벨.
 *          nullable (Operation 미부착/404/v1.4 이전 데이터)
 */

import { apiClient } from './client';

/**
 * 액터 유형
 * - GUARDIAN: 보호자 (앱 USER)
 * - TEACHER: 선생님
 * - BUSINESS: 사업주(원장)
 * - ADMIN: 어드민
 * - ANONYMOUS: 비로그인 / 토큰 없음 / 만료
 */
export type ApiActorType =
  | 'GUARDIAN'
  | 'TEACHER'
  | 'BUSINESS'
  | 'ADMIN'
  | 'ANONYMOUS';

/**
 * 성공 여부
 * - Y: HTTP 2xx + 예외 없음 + ServiceResult.codeEnum.status 200/201
 * - N: 위 조건 중 하나라도 실패
 */
export type SuccessYn = 'Y' | 'N';

/**
 * 감사 로그 목록 검색 요청
 * - 모든 필드는 선택 (서버 정렬은 logSeq DESC 고정)
 */
export interface AuditLogSearchRequest {
  /** 시작 일시 (이상). ISO-8601 KST. */
  dtFrom?: string;
  /** 종료 일시 (미만). ISO-8601 KST. */
  dtTo?: string;
  actorType?: ApiActorType;
  /** 보호자 시퀀스 */
  userSeq?: number;
  /** 사업자/선생님 시퀀스 */
  bizUserSeq?: number;
  /** 사업장 시퀀스 */
  companySeq?: number;
  /** 펫 시퀀스 */
  petSeq?: number;
  /** 티켓 시퀀스 */
  ticketSeq?: number;
  /** 보호자 이름 부분일치 (LIKE, 대소문자 무시) — v1.2 추가 */
  userName?: string;
  /** 사업자/선생님 이름 부분일치 (LIKE, 대소문자 무시) — v1.2 추가 */
  bizUserName?: string;
  /** 사업장명 부분일치 (LIKE, 대소문자 무시) — v1.2 추가 */
  companyName?: string;
  /** 반려동물 이름 부분일치 (LIKE, 대소문자 무시) — v1.2 추가 */
  petName?: string;
  /** HTTP 메소드 (GET/POST/PUT/PATCH/DELETE) */
  httpMethod?: string;
  /** URI 부분일치 (LIKE) */
  uriContains?: string;
  /** URI 패턴 완전일치 */
  uriPattern?: string;
  successYn?: SuccessYn;
  /** HTTP 상태코드 */
  httpStatus?: number;
  /** 에러 코드 */
  errorCode?: string;
  /** 추적 ID (단건 조회) */
  traceId?: string;
  /** 0-base 페이지 번호 (기본 0) */
  page?: number;
  /** 페이지 크기 (1~100, 기본 20) */
  size?: number;
}

/**
 * 감사 로그 목록 아이템 (요약)
 */
export interface AuditLogListItem {
  logSeq: number;
  traceId: string;
  httpMethod: string;
  requestUri: string;
  uriPattern: string;
  /**
   * 어드민 가독용 API 이름 — v1.4 추가.
   * - `@Operation summary` + `@Tag name` 조합 (예: `"User-Reserve > 예약 생성"`)
   * - nullable: @Operation 미부착, 404/403 스캐너 트래픽, v1.4 이전 데이터.
   *   UI 는 항상 fallback 처리 (null → httpMethod + requestUri 표시) 필요
   */
  apiSummary: string | null;
  httpStatus: number;
  successYn: SuccessYn;
  durationMs: number;
  cdActorType: ApiActorType;
  userSeq: number | null;
  userName: string | null;
  bizUserSeq: number | null;
  bizUserName: string | null;
  companySeq: number | null;
  companyName: string | null;
  petSeq: number | null;
  petName: string | null;
  ticketSeq: number | null;
  successMessage: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  dtRequest: string;
  dtResponse: string;
}

/**
 * Spring Page 응답 래퍼
 */
export interface AuditLogPage {
  content: AuditLogListItem[];
  pageable?: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * 감사 로그 상세
 * - 목록 응답 필드 + 요청/응답 본문, 헤더, 스택트레이스
 */
export interface AuditLogDetail extends AuditLogListItem {
  queryString: string | null;
  handlerMethod: string | null;
  clientIp: string | null;
  userAgent: string | null;
  referer: string | null;
  appVersion: string | null;
  platform: string | null;
  mainUserSeq: number | null;
  exceptionClass: string | null;
  requestSize: number | null;
  responseSize: number | null;
  /** JSON 문자열 (민감 헤더는 ***MASKED*** 처리) */
  requestHeaders: string | null;
  /** 요청 본문 (민감 필드 자동 마스킹) */
  requestBody: string | null;
  /** 응답 본문 (민감 필드 자동 마스킹) */
  responseBody: string | null;
  /** 실패 시 스택트레이스 상위 30줄 */
  errorStack: string | null;
}

/**
 * 액터별 카운트
 */
export interface ActorCount {
  actorType: ApiActorType;
  count: number;
}

/**
 * 실패 URI Top N
 */
export interface TopErrorUri {
  uriPattern: string;
  httpMethod: string;
  count: number;
}

/**
 * 통계 응답
 */
export interface AuditLogStats {
  totalCount: number;
  successCount: number;
  failureCount: number;
  actorCounts: ActorCount[];
  topErrorUris: TopErrorUri[];
}

/**
 * 통계 요청 파라미터
 */
export interface AuditLogStatsParams {
  /** 시작 일시 (이상). ISO-8601 KST. */
  dtFrom?: string;
  /** 종료 일시 (미만). ISO-8601 KST. */
  dtTo?: string;
  /** 실패 URI Top N 개수 (기본 10) */
  topN?: number;
}

/**
 * ServiceResult 래퍼에서 payload 추출
 * - 백엔드는 `{ codeEnum, data }` 형식
 * - 일부 환경에서 `{ statusCode, data: { codeEnum, data } }` 로 한 번 더 래핑되는 경우가 있어 양쪽 모두 대응
 */
function unwrap<T>(response: any): T {
  if (response == null) return response as T;
  // BaseResponse(외부) + ServiceResult(내부) 이중 래핑
  if (
    response.data &&
    typeof response.data === 'object' &&
    'data' in response.data &&
    'codeEnum' in response.data
  ) {
    return response.data.data as T;
  }
  // 단일 ServiceResult
  if (response.data !== undefined) {
    return response.data as T;
  }
  return response as T;
}

/**
 * undefined / 빈 문자열 / null 값을 제거한 새 객체 반환.
 * - axios `params` 는 undefined 키도 그대로 직렬화하지는 않지만,
 *   불필요한 빈 문자열 키가 쿼리스트링에 포함되는 걸 막기 위해 명시 정리.
 */
function compactParams<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    out[k] = v;
  });
  return out;
}

/**
 * 감사 로그 API 서비스
 */
export const auditLogsApi = {
  /**
   * 감사 로그 목록 조회 (GET + 쿼리스트링)
   * - v1.2: POST → GET 환원.
   *   POST 호출 시 그 호출 자체가 다시 감사 로그로 적재되어 폭주하는 문제를 회피하기 위해,
   *   백엔드는 `/api/admin/audit/**` 을 화이트리스트로 적재 제외 + GET 으로 통일.
   */
  search: async (
    params: AuditLogSearchRequest = {},
  ): Promise<AuditLogPage> => {
    const response = await apiClient.get('/admin/audit/api-logs/search', {
      params: compactParams(params),
    });
    return unwrap<AuditLogPage>(response);
  },

  /**
   * 감사 로그 상세 조회
   * - 존재하지 않는 logSeq 인 경우 codeEnum=NOT_FOUND, data=null
   */
  getDetail: async (logSeq: number): Promise<AuditLogDetail | null> => {
    const response = await apiClient.get(`/admin/audit/api-logs/${logSeq}`);
    return unwrap<AuditLogDetail | null>(response);
  },

  /**
   * 감사 로그 통계 (대시보드용)
   */
  getStats: async (
    params: AuditLogStatsParams = {},
  ): Promise<AuditLogStats> => {
    const response = await apiClient.get('/admin/audit/api-logs/stats', {
      params,
    });
    return unwrap<AuditLogStats>(response);
  },
};
